import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import * as _ from 'lodash';
import { AlertMessageModel, results } from 'src/app/common/models/common.model';
import { AlertMessageService } from 'src/app/common/services/alert-message.service';
import { SnapshotService } from '../Services/snapshot.service';
import { EntrypointcolorService } from 'src/app/common/services/entrypointcolor.service';

@Component({
  selector: 'app-selection-summary-view',
  templateUrl: './selection-summary-view.component.html',
  styleUrls: ['./selection-summary-view.component.css'],
})
export class SelectionSummaryViewComponent {
  @Output() isSavePopUpVisible = new EventEmitter<any>();
  @Output() SaveSelectionClick = new EventEmitter<any>();
  @Input() Header: string = '';
  @Input() isSelectionSummaryClick: boolean = false;
  @Input() isSaveSelectionClick: boolean = false;
  SaveSelection: string = '';
  selectionSummary: String = '';
  headerText: String = '';

  // New structured data model
  titleSection: { key: string; value: string }[] = [];
  entryPointSections: { header: string; value: string }[] = [];
  selections: { key: string; value: string }[] = [];

  constructor(
    private _alertService: AlertMessageService,
    public _configService: SnapshotService,
    public entrypointColorService: EntrypointcolorService,
  ) {}

  // Keep in sync with leftpanel productCategoryLabel display values.
  private readonly secondaryEntryPointSummaryLabels = [
    'Product Category',
    'Account Types',
    'Broad Market Categories',
  ];

  ngOnInit() {
    this.selectionSummary = this._configService.selectionSummaryTextTitle;
    this.headerText = this.Header;
    this.GenerateSelectionSummary();
    this.AddBrandToSelectionSummary();
  }

  GenerateSelectionSummary() {
    this.titleSection = [];
    this.entryPointSections = [];
    this.selections = [];

    const selectionSummaryArray = this.selectionSummary
      .split('||')
      .map((section) => section.trim())
      .filter(Boolean);
    let foundSelections = false;

    for (const section of selectionSummaryArray) {
      if (section.startsWith('Compare Across:')) {
        const titleContent = section.replace('Compare Across:', '').trim();
        const titleParts = titleContent.split(':');

        if (titleParts.length > 1) {
          this.titleSection.push({
            key: titleParts[0].trim(),
            value: titleParts.slice(1).join(':').trim(),
          });
        } else {
          this.titleSection.push({
            key: '',
            value: titleContent,
          });
        }
      } else if (section.startsWith('Entry Point:')) {
        this.parseEntryPointItems(
          section.replace('Entry Point:', '').trim(),
        );
      } else if (section.startsWith('Selections:')) {
        foundSelections = true;
        this.parseSelectionItems(section.replace('Selections:', '').trim());
      } else if (!foundSelections) {
        this.parseEntryPointItems(section);
      } else if (this.isSecondaryEntryPointSummarySection(section)) {
        const parsed = this.parseKeyValueSection(section);
        if (parsed) {
          this.addEntryPointSection(parsed.key, parsed.value);
        }
      } else {
        const parsed = this.parseKeyValueSection(section);
        if (parsed) {
          this.selections.push(parsed);
        }
      }
    }

    this.selections = this.selections.filter(
      (selection) =>
        !this.entryPointSections.some(
          (entryPoint) =>
            entryPoint.header.toLowerCase() === selection.key.toLowerCase() &&
            entryPoint.value.toLowerCase() === selection.value.toLowerCase(),
        ),
    );
  }

  private parseKeyValueSection(
    section: string,
  ): { key: string; value: string } | null {
    const parts = section.split(':');
    if (parts.length < 2) {
      return null;
    }

    return {
      key: parts[0].trim(),
      value: parts.slice(1).join(':').trim(),
    };
  }

  private isSecondaryEntryPointSummarySection(section: string): boolean {
    if (section.startsWith('Audience:')) {
      return true;
    }

    return this.secondaryEntryPointSummaryLabels.some((label) =>
      section.startsWith(`${label}:`),
    );
  }

  private parseEntryPointItems(entryPointContent: string): void {
    entryPointContent
      .split(';')
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((item) => {
        const parsed = this.parseKeyValueSection(item);
        if (parsed) {
          this.addEntryPointSection(parsed.key, parsed.value);
        }
      });
  }

  private parseSelectionItems(selectionsText: string): void {
    const items = selectionsText
      .split('|')
      .map((item) => item.trim())
      .filter(Boolean);

    let filtersEntry: { key: string; value: string } | null = null;

    for (const item of items) {
      const parsed = this.parseKeyValueSection(item);
      if (!parsed) {
        continue;
      }
      if (parsed.key === 'Filters') {
        filtersEntry = parsed;
        this.selections.push(filtersEntry);
      } else if (filtersEntry !== null) {
        // Append subsequent pipe-items to the Filters entry
        filtersEntry.value += ' | ' + item.trim();
      } else {
        this.selections.push(parsed);
      }
    }
  }

  private addEntryPointSection(header: string, value: string): void {
    const exists = this.entryPointSections.some(
      (entryPoint) =>
        entryPoint.header.toLowerCase() === header.toLowerCase() &&
        entryPoint.value === value,
    );

    if (!exists) {
      this.entryPointSections.push({ header, value });
    }
  }

  AddBrandToSelectionSummary() {
    // Get the brand value from the finalLeftPanelSubmitObject
    const brandValue =
      this._configService?.finalLeftPanelSubmitObject?.previewData
        ?.Audiencebrand?.[0]?.option;

    if (brandValue) {
      // Remove the pipe and use the last part
      const rawBrandName = brandValue.split('|').pop() ?? brandValue;
      const brandName = `${rawBrandName}`;

      // Add brand to the selections array
      this.selections.push({
        key: 'Brand',
        value: brandName,
      });
    }
  }

  AddAudienceToSelectionSummary() {
    const audienceSummary = this._configService.audienceSummary;
    if (audienceSummary && audienceSummary !== 'No selections') {
      const plainText = audienceSummary
        .replace(/<br>/gi, ', ')
        .replace(/<[^>]+>/g, '');
      this.addEntryPointSection('Audience', plainText);
      this.selections = this.selections.filter(
        (selection) => selection.key.toLowerCase() !== 'audience',
      );
    }
  }

  OnCloseClick() {
    this.isSaveSelectionClick = false;
    this.SaveSelection = '';
    this.isSavePopUpVisible.emit({
      isClose: false,
    });
  }

  onSaveBtnClick() {
    if (this.SaveSelection == '') {
      this.errorPopup('Selection name cannot be empty.');
    } else {
      this.SaveSelectionClick.emit({
        isValidationPass: true,
        selectionName: this.SaveSelection,
        isShareSelection: false, //this is for save selection
        selectionType: 'userDefined', //only user defined can be saved
        Audiencebrand:
          this._configService.finalLeftPanelSubmitObject?.previewData
            ?.Audiencebrand || null,
      });
    }
  }

  errorPopup(message: string, headerTxt: string = 'Error'): void {
    const alertObj: AlertMessageModel = {
      header: headerTxt,
      message: message,
      show: true,
    };
    this._alertService.showAlert(alertObj);
  }

  onKeyDown(event: KeyboardEvent): void {
    // Get the pressed key
    const pressedKey = event.key;

    // Allow alphanumeric characters, space, and backspace; prevent other keys

    //onst isValidKey = /^[A-Za-z0-9\s\-_]+$/.test(pressedKey);

    //const isValidKey = /^[^\s@]+$/.test(pressedKey);
    const isValidKey = /^[^\@]+$/.test(pressedKey) || pressedKey === ' ';

    if (!isValidKey) {
      event.preventDefault();
    }
  }
}
